import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import type { Question } from '@/types/form'
import type { ReactNode } from 'react'
import { CommonSettings } from './Settings/CommonSettings'
import { DateSettings } from './Settings/DateSettings'
import { FileUploadSettings } from './Settings/FileUploadSettings'
import { LayoutBlockSettings } from './Settings/LayoutBlockSettings'
import { MatrixSettings } from './Settings/MatrixSettings'
import { MultipleChoiceSettings } from './Settings/MultipleChoiceSettings'
import { NPSSettings } from './Settings/NPSSettings'
import { NestedSelectSettings } from './Settings/NestedSelectSettings'
import { NumberSettings } from './Settings/NumberSettings'
import { RatingSettings } from './Settings/RatingSettings'
import { TextSettings } from './Settings/TextSettings'
import { TotalSettings } from './Settings/TotalSettings'

type QuestionSettingsFieldsProps = {
  question: Question
  questions: Question[]
  disabled?: boolean
  readonlyNotice?: ReactNode
  showButtonTextSetting?: boolean
  onQuestionChange: (patch: Partial<Question>) => void
  onSettingsChange: (patch: Partial<Question['settings']>) => void
}

export function QuestionSettingsFields({
  question,
  questions,
  disabled = false,
  readonlyNotice,
  showButtonTextSetting = false,
  onQuestionChange,
  onSettingsChange,
}: QuestionSettingsFieldsProps) {
  function updateQuestion(patch: Partial<Question>) {
    if (disabled) return
    onQuestionChange(patch)
  }

  function updateSettings(patch: Partial<Question['settings']>) {
    if (disabled) return
    onSettingsChange(patch)
  }

  return (
    <div className="flex flex-col gap-5">
      {readonlyNotice}
      <div className={cn('flex flex-col gap-5', disabled && 'pointer-events-none opacity-60')}>
        <SettingsBlock title="題目內容">
          <div className="flex flex-col gap-2">
            <Label>問題標題</Label>
            <Input
              value={question.title}
              onChange={(event) => updateQuestion({ title: event.target.value })}
              placeholder="標題"
              aria-label="問題標題"
              disabled={disabled}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label>問題描述</Label>
            <Textarea
              value={question.description ?? ''}
              onChange={(event) => updateQuestion({ description: event.target.value })}
              placeholder="描述"
              aria-label="問題描述"
              className="min-h-24"
              disabled={disabled}
            />
          </div>
        </SettingsBlock>
        <SettingsBlock title="基本設定">
          <CommonSettings
            question={question}
            onQuestionChange={updateQuestion}
            onSettingsChange={updateSettings}
            showButtonTextSetting={showButtonTextSetting}
          />
        </SettingsBlock>
        {['MULTIPLE_CHOICE', 'CHECKBOXES', 'DROPDOWN', 'PICTURE_CHOICE', 'RANKING'].includes(
          question.type,
        ) && (
          <SettingsBlock title="選項設定">
            <MultipleChoiceSettings
              question={question}
              onQuestionChange={updateQuestion}
              onChange={updateSettings}
            />
          </SettingsBlock>
        )}
        {[
          'SHORT_TEXT',
          'LONG_TEXT',
          'EMAIL',
          'PHONE_NUMBER',
          'TAIWAN_MOBILE',
          'TAIWAN_LANDLINE',
          'WEBSITE',
        ].includes(question.type) && (
          <SettingsBlock title="文字與驗證">
            <TextSettings question={question} onChange={updateSettings} />
          </SettingsBlock>
        )}
        {['NUMBER', 'NUMBER_SLIDER'].includes(question.type) && (
          <SettingsBlock title="數值設定">
            <NumberSettings question={question} onChange={updateSettings} />
          </SettingsBlock>
        )}
        {question.type === 'TOTAL' && (
          <SettingsBlock title="計算設定">
            <TotalSettings question={question} questions={questions} onChange={updateSettings} />
          </SettingsBlock>
        )}
        {question.type === 'NESTED_SELECT' && (
          <SettingsBlock title="多層選單設定">
            <NestedSelectSettings
              question={question}
              onQuestionChange={updateQuestion}
              onChange={updateSettings}
            />
          </SettingsBlock>
        )}
        {['RATING', 'OPINION_SCALE'].includes(question.type) && (
          <SettingsBlock title="量表設定">
            <RatingSettings question={question} onChange={updateSettings} />
          </SettingsBlock>
        )}
        {question.type === 'NPS' && (
          <SettingsBlock title="NPS 設定">
            <NPSSettings question={question} onChange={updateSettings} />
          </SettingsBlock>
        )}
        {['DATE', 'TIME'].includes(question.type) && (
          <SettingsBlock title="日期時間設定">
            <DateSettings question={question} onChange={updateSettings} />
          </SettingsBlock>
        )}
        {question.type === 'FILE_UPLOAD' && (
          <SettingsBlock title="檔案上傳設定">
            <FileUploadSettings question={question} onChange={updateSettings} />
          </SettingsBlock>
        )}
        {question.type === 'MATRIX' && (
          <SettingsBlock title="矩陣設定">
            <MatrixSettings question={question} onChange={updateSettings} />
          </SettingsBlock>
        )}
        {['STATEMENT', 'SECTION_TITLE', 'INTRO_BLOCK', 'DIVIDER'].includes(question.type) && (
          <SettingsBlock title="版面區塊設定">
            <LayoutBlockSettings question={question} onChange={updateSettings} />
          </SettingsBlock>
        )}
      </div>
    </div>
  )
}

function SettingsBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 transition-colors hover:border-indigo-200 hover:bg-indigo-50/30">
      <h3 className="text-sm font-semibold text-slate-950">{title}</h3>
      {children}
    </section>
  )
}
